/**
 * Mezereon collection-card quick-add.
 *
 * Click delegate on `.mz-quick-add-trigger` inside Vue-rendered Mezereon cards.
 * Fetches GET /products/{handle}?section_id=api-card-quick-add, injects the
 * returned HTML into the per-card slot, then opens the existing
 * <card-product-variants> slide-up panel. The injected snippet auto-wires
 * its own custom elements (cardVariants + QuickAddProduct) on connection,
 * so add-to-cart submission flows through the standard `theme:cart:add`
 * pipeline without any extra JS here.
 */
(function () {
  'use strict';

  var SECTION_ID = 'api-card-quick-add';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cache = new Map();

  function endpoint(handle) {
    return ROOT.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_ID;
  }

  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function getPortal() {
    var portal = document.getElementById('mz-quick-add-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'mz-quick-add-portal';
      portal.className = 'mz-quick-add-slot mz-quick-add-portal';
      document.body.appendChild(portal);
    }
    return portal;
  }

  // On desktop the sheet renders inside the card image area; on mobile it
  // mounts in a body-level portal so `position: fixed` is relative to the
  // viewport (`.mz-item-card` uses transform which traps fixed children).
  function findSlot(trigger) {
    if (isMobile()) return getPortal();
    var card = trigger.closest('.mz-grid-item') || trigger.closest('.mz-item-card');
    if (!card) return null;
    return card.querySelector('.mz-quick-add-slot');
  }

  function lockBody() {
    if (isMobile()) document.documentElement.classList.add('mz-quick-add-locked');
  }

  function unlockBody() {
    if (!document.querySelector('.mz-quick-add-slot.is-open')) {
      document.documentElement.classList.remove('mz-quick-add-locked');
    }
  }

  function openPanel(slot) {
    var panel = slot.querySelector('card-product-variants');
    if (!panel) return;
    slot.classList.add('is-open');
    lockBody();
    // Next frame so the freshly-inserted node has applied its base transform
    // before we toggle `.active` — gives us the slide-up transition.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (typeof panel.show === 'function') {
          panel.show();
        } else {
          panel.classList.add('active');
          var item = panel.closest('.product-item');
          if (item) item.classList.add('show__variant__item');
        }
      });
    });
  }

  function closePanel(slot) {
    var panel = slot.querySelector('card-product-variants');
    if (!panel) return;
    if (typeof panel.hide === 'function') {
      panel.hide();
    } else {
      panel.classList.remove('active');
      var item = panel.closest('.product-item');
      if (item) item.classList.remove('show__variant__item');
    }
    // On mobile the slide-down transform is driven by `.is-open` staying on
    // the slot — wait for the animation before tearing it down.
    var teardown = function () {
      slot.classList.remove('is-open');
      unlockBody();
    };
    if (isMobile()) {
      setTimeout(teardown, 320);
    } else {
      teardown();
    }
  }

  function extractHost(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var host = doc.querySelector('.mz-quick-add-host');
    if (!host) return null;
    // Server renders the panel with `.active` so the standalone /products/{handle}
    // page would show it. In our context we want it hidden until the user clicks
    // and we play the slide-up animation.
    var panel = host.querySelector('card-product-variants');
    if (panel) panel.classList.remove('active');
    return host;
  }

  async function loadInto(slot, handle) {
    var html;
    if (cache.has(handle)) {
      html = cache.get(handle);
    } else {
      var res = await fetch(endpoint(handle), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('quick-add fetch failed: ' + res.status);
      html = await res.text();
      cache.set(handle, html);
    }
    var host = extractHost(html);
    if (!host) throw new Error('quick-add response missing .mz-quick-add-host');
    slot.replaceChildren(host);
  }

  function handleTriggerClick(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var trigger = target.closest('.mz-quick-add-trigger');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    var handle = trigger.getAttribute('data-product-handle');
    if (!handle) {
      console.warn('[mz-quick-add] trigger missing data-product-handle');
      return;
    }

    var slot = findSlot(trigger);
    if (!slot) {
      console.warn('[mz-quick-add] no .mz-quick-add-slot near trigger');
      return;
    }

    if (trigger.dataset.busy === '1') return;
    trigger.dataset.busy = '1';
    trigger.classList.add('is-loading');

    loadInto(slot, handle)
      .then(function () { openPanel(slot); })
      .catch(function (err) { console.error('[mz-quick-add]', err); })
      .finally(function () {
        trigger.dataset.busy = '0';
        trigger.classList.remove('is-loading');
      });
  }

  // Capture phase so Vue/Mezereon handlers calling stopPropagation can't swallow it.
  document.addEventListener('click', handleTriggerClick, true);

  // The internal X (rendered inside card-product-variants) calls panel.hide()
  // which removes `.active` but leaves our slot's `.is-open` flag set. Defer
  // the cleanup so the mobile slide-down animation can finish.
  document.addEventListener('click', function (e) {
    var cross = e.target && e.target.closest && e.target.closest('.cross__svg__repo.card__product');
    if (!cross) return;
    var slot = cross.closest('.mz-quick-add-slot');
    if (!slot) return;
    var teardown = function () {
      slot.classList.remove('is-open');
      unlockBody();
    };
    if (isMobile()) {
      setTimeout(teardown, 320);
    } else {
      teardown();
    }
  });

  // Backdrop click on the mobile bottom-sheet closes the panel.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.classList) return;
    if (t.classList.contains('mz-quick-add-slot') && t.classList.contains('is-open')) {
      closePanel(t);
    }
  });

  // Close any open panel after a successful cart add.
  document.addEventListener('theme:product:add', function () {
    document.querySelectorAll('.mz-quick-add-slot.is-open').forEach(closePanel);
  });
})();
